import {useEffect, useState} from 'react';
import {load} from 'exifreader';
import {Box, Heading, Link, Separator, Text, VStack} from '@chakra-ui/react';
import {DriveFile} from '../drive/types';
import {formatDisplayDateTime} from '../domain/naming';
import {ExifSummary, formatDimensions, hasExifDetails, summarizeDriveMetadata, summarizeExifTags} from '../domain/exif';
import {formatAspectRatioShort} from '../domain/aspectRatio';
import {formatFileSize} from '../utils/formatFileSize';

interface ExifSidebarProps {
    file: DriveFile;
    /** The original file — only read for images Drive has no `imageMediaMetadata` for. */
    blob: Blob | undefined;
    /** Loading `blob` failed. */
    blobError?: Error | null;
    /** The original won't be read (a format the viewer never downloads, e.g. RAW). */
    originalUnreadable?: boolean;
}

function formatIsoDateTime(iso: string | undefined): string | undefined {
    if (!iso) return undefined;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return undefined;
    return formatDisplayDateTime(date);
}

/** EXIF parsed out of the original file: undefined while pending, null if it couldn't be read. */
function useBlobExifSummary(blob: Blob | undefined): ExifSummary | null | undefined {
    // Kept with the blob it came from, so a new photo never renders the previous photo's summary.
    const [parsed, setParsed] = useState<{ blob: Blob; summary: ExifSummary | null }>();

    useEffect(() => {
        if (!blob) return;

        let cancelled = false;
        blob.arrayBuffer().then((buffer) => {
            if (cancelled) return;
            let summary: ExifSummary | null;
            try {
                summary = summarizeExifTags(load(buffer, {expanded: true}));
            } catch {
                summary = null;
            }
            setParsed({blob, summary});
        });
        return () => {
            cancelled = true;
        };
    }, [blob]);

    return parsed && parsed.blob === blob ? parsed.summary : undefined;
}

export function ExifSidebar({file, blob, blobError, originalUnreadable}: ExifSidebarProps) {
    const driveMetadata = file.imageMediaMetadata;
    // The listing already carries Drive's metadata for almost every photo; the original is a fallback.
    const blobSummary = useBlobExifSummary(driveMetadata ? undefined : blob);
    const summary = driveMetadata ? summarizeDriveMetadata(driveMetadata) : blobSummary;
    const hasDetails = !!summary && hasExifDetails(summary);

    let status: string | undefined;
    if (!driveMetadata && originalUnreadable) status = 'Nem találhatók EXIF-metaadatok.';
    else if (!driveMetadata && blobError) status = 'A metaadatok beolvasása sikertelen.';
    else if (!driveMetadata && !blob) status = 'Kép betöltése…';
    else if (summary === undefined) status = 'Metaadatok beolvasása…';
    else if (!hasDetails) status = 'Nem találhatók EXIF-metaadatok.';

    const fileSize = file.size ? formatFileSize(Number(file.size)) : undefined;
    const owner = file.owners?.[0]?.displayName;
    const created = formatIsoDateTime(file.createdTime);
    const modified = formatIsoDateTime(file.modifiedTime);

    return <VStack
        align="stretch"
        gap={3}
        p={4}
        bg="gray.900"
        color="white"
    >
        <Heading size="sm">Fájl</Heading>
        <Separator borderColor="whiteAlpha.300"/>
        <ExifRow label="Méret" value={fileSize}/>
        <ExifRow label="Tulajdonos" value={owner}/>
        <ExifRow label="Létrehozva" value={created}/>
        <ExifRow label="Módosítva" value={modified}/>

        <Heading size="sm" mt={2}>EXIF</Heading>
        <Separator borderColor="whiteAlpha.300"/>
        <ExifRow
            label="Felbontás"
            value={summary?.dimensions
                && `${formatDimensions(summary.dimensions)} (${formatAspectRatioShort(summary.dimensions)})`}
        />
        {status && <Text fontSize="sm" color="gray.400">{status}</Text>}
        {summary && hasDetails && <>
            <ExifRow label="Fényképezőgép" value={summary.camera}/>
            <ExifRow label="Objektív" value={summary.lens}/>
            <ExifRow label="Készült" value={summary.dateTaken}/>
            <ExifRow label="Záridő" value={summary.exposure}/>
            <ExifRow label="Rekeszérték" value={summary.aperture}/>
            <ExifRow label="ISO" value={summary.iso}/>
            <ExifRow label="Gyújtótávolság" value={summary.focalLength}/>
            {summary.gps && (
                <Box>
                    <Text fontSize="xs" color="gray.400">Helyszín</Text>
                    <Link
                        href={`https://www.google.com/maps?q=${summary.gps.lat},${summary.gps.lon}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="blue.300"
                        fontSize="sm"
                    >
                        {summary.gps.lat.toFixed(5)}, {summary.gps.lon.toFixed(5)}
                    </Link>
                </Box>
            )}
        </>}
    </VStack>;
}

function ExifRow({label, value}: { label: string; value: string | undefined }) {
    if (!value) return null;
    return <Box>
        <Text fontSize="xs" color="gray.400">{label}</Text>
        <Text fontSize="sm">{value}</Text>
    </Box>;
}
